import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Paper, Switch, TextField } from '@mui/material'
import { DatePicker } from '@carsys/ui/src'
import moment, { Moment } from 'moment'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { useNotifier } from '@cbgms/base/notifier'
import { fetchConcepts, saveConcepts } from './state/actions'
import { inputBaseClasses } from '@mui/material/InputBase'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { IRootReducerState } from 'state/reducers'

interface Module {
  Overridden: any
  ModuleType: unknown
  ModuleId: string
  ModuleName: string
  IsSearchFilter: boolean
  AppRole: string
  IsBasePackage: boolean
  IsEnabled: boolean
  StartOfContract?: string
  EndOfContract?: string
}

const commonCellStyle = {
  padding: '2px',
  fontSize: '14px',
  color: '#808080',
  whiteSpace: 'nowrap'
}

const dateFieldStyle = {
  [`& .${inputBaseClasses.root}`]: { height: '30px', fontSize: '12px' }
}

const headerRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginX: 1, marginY: 1 }

const actionGroupStyle = { display: 'flex', alignItems: 'center', gap: '4px' }

const searchFieldStyle = { width: '300px', [`& .${inputBaseClasses.root}`]: { height: '35px', fontSize: '14px' } }

const actionButtonStyle = { height: '35px' }

const backButtonStyle = { height: '35px', marginBottom: '1px' }

const backIconStyle = { marginTop: '-10px' }

const saveButtonStyle = { marginBottom: '5px' }

const gridBaseStyle = {
  ...commonCellStyle,
  '& .over-riddeden': {
    backgroundColor: '#F8D7DA'
  },
  '& .evenRow': {
    backgroundColor: '#f4f4f4'
  },
  '& .oddRow': {
    backgroundColor: 'inherit'
  },
  '& .cell-data:focus': {
    outline: 'none'
  },
  '& .cell-data:focus-within': {
    outline: 'none'
  }
}

const dateCellBoxStyle = { height: '30px', padding: 0 }

const calendarIconStyle = { fontSize: '16px' }

const moduleTypeTextFieldStyle = {
  fontSize: '14px',
  height: '30px',
  [`& .${inputBaseClasses.root}`]: {
    height: '30px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
  },
  [`& .${inputBaseClasses.input}`]: {
    textAlign: 'center'
  }
}

const ConceptManagement: React.FC = () => {
  const ou_name = useSelector((state: IRootReducerState) => state.auth?.user?.ou_name)
  const AppId = useSelector((state: IRootReducerState) => state.auth?.user?.AppId)
  const [loading, setLoading] = useState(false)
  const [modules, setModules] = useState<Module[]>([])
  const [sortedModules, setSortedModules] = useState<Module[]>([])
  const [updatedModules, setUpdatedModules] = useState<Module[]>([])
  const [activeRowId, setActiveRowId] = useState<string | null>(null)
  const location = useLocation<{ customerId: string; name: string; address: string; custEosId: string; shipToLoc: string }>()
  const { custEosId, shipToLoc, customerId, name } = location.state || { custEosId: '', shipToLoc: '', customerId: '', name: '' }
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>('')
  const [tableHeight, setTableHeight] = useState<number>(700)
  const tableRef = React.useRef<HTMLDivElement>(null)
  const notifier = useNotifier()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMounted = useRef(true)
  const paperStyle = { height: tableHeight, border: '1px solid #ccc', backgroundColor: 'white', marginTop: '5px' }
  const gridStyle = { ...gridBaseStyle, height: tableHeight - 50 }

  const fetchConceptsData = useCallback(async () => {
    const appId = AppId
    try {
      setLoading(true)
      const conceptData = await dispatch(fetchConcepts(custEosId, appId))
      setModules(conceptData.Data)
    } catch (error) {
      notifier.error('Error fetching concepts')
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }, [dispatch, custEosId, notifier])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])
  useEffect(() => {
    fetchConceptsData()
  }, [fetchConceptsData])

  useEffect(() => {
    if (isMounted.current) {
      setSortedModules([...modules].sort((_a, b) => (b.IsEnabled ? 1 : -1)))
    }
  }, [modules])

  const filteredModules = searchTerm
    ? sortedModules.filter(
        m => m.ModuleId.toLowerCase().includes(searchTerm.toLowerCase()) || m.ModuleName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : sortedModules

  const handleSearch = () => {
    setSearchTerm(inputValue.trim())
  }

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        setTableHeight(window.innerHeight - tableRef.current.getBoundingClientRect().top - 35)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [tableRef.current])
  const handleDateChange = (
    moduleId: string,
    startOfContract: string | undefined,
    date: Moment | null,
    field: 'StartOfContract' | 'EndOfContract'
  ) => {
    if (date !== null) {
      const formattedDate = date.format('YYYY-MM-DD')
      setModules(prevModules =>
        prevModules.map(module => {
          if (module.ModuleId === moduleId && module.StartOfContract === startOfContract) {
            const updatedModule = { ...module, [field]: formattedDate }
            setUpdatedModules(prevUpdatedModules => {
              const existingIndex = prevUpdatedModules.findIndex(
                mod => mod.ModuleId === moduleId && mod.StartOfContract === startOfContract
              )
              if (existingIndex >= 0) {
                const updated = [...prevUpdatedModules]
                updated[existingIndex] = updatedModule
                return updated
              }
              return [...prevUpdatedModules, updatedModule]
            })
            return updatedModule
          }
          return module
        })
      )
    }
  }
  function renderStartDateOfContract(params: GridRenderCellParams<any, string>, handleDateChange: any) {
    return (
      <Box sx={dateCellBoxStyle} data-testid={`module-start-${params.value}`}>
        <DatePicker
          name={`module-start-${params.row.ModuleID}`}
          value={params.value ? moment(params.value) : null}
          components={{
            OpenPickerIcon: () => <i className='calendar icon' aria-hidden='true' style={calendarIconStyle} />
          }}
          sx={dateFieldStyle}
          onChange={date => handleDateChange(params.row.ModuleId, params.row.StartOfContract, date, 'StartOfContract')}
          readOnly
        />
      </Box>
    )
  }
  function rendeEndDateOfContract(params: GridRenderCellParams<any, string>, handleDateChange: any) {
    return (
      <Box sx={dateCellBoxStyle} data-testid={`module-start-${params.value}`}>
        <DatePicker
          name={`module-end-${params.row.ModuleID}`}
          value={params.value ? moment(params.value) : null}
          components={{
            OpenPickerIcon: () => <i className='calendar icon' aria-hidden='true' style={calendarIconStyle} />
          }}
          sx={dateFieldStyle}
          onChange={date => handleDateChange(params.row.ModuleId, params.row.StartOfContract, date, 'EndOfContract')}
          minDate={moment()}
        />
      </Box>
    )
  }
  function renderModuleType(params: GridRenderCellParams<any, string>) {
    return (
      <Box sx={dateCellBoxStyle} data-testid={`module-type-${params.value}`}>
        <TextField sx={moduleTypeTextFieldStyle} value={params.value} InputProps={{ style: { textAlign: 'center' } }} />
      </Box>
    )
  }
  const renderIsActive = (params: GridRenderCellParams<any, boolean>) => {
    const handleChange = () => {
      const moduleId = params.row.ModuleId
      const startOfContract = params.row.StartOfContract
      setModules(prevModules =>
        prevModules.map(module => {
          if (module.ModuleId === moduleId && module.StartOfContract === startOfContract) {
            const updatedModule = {
              ...module,
              IsEnabled: !module.IsEnabled,
              StartOfContract: !module.IsEnabled && !module.StartOfContract ? moment().format('YYYY-MM-DD') : module.StartOfContract
            }
            setUpdatedModules(prevUpdatedModules => {
              const existingIndex = prevUpdatedModules.findIndex(
                mod => mod.ModuleId === moduleId && mod.StartOfContract === startOfContract
              )
              if (existingIndex >= 0) {
                const updated = [...prevUpdatedModules]
                updated[existingIndex] = updatedModule
                return updated
              }
              return [...prevUpdatedModules, updatedModule]
            })
            return updatedModule
          }
          return module
        })
      )
    }
    return <Switch id={params.row.ModuleId + params.row.StartOfContract} checked={params.value} onChange={handleChange} />
  }
  const columns: GridColDef<Module>[] = [
    {
      field: 'ModuleId',
      headerName: t('editConcept.ConceptID'),
      width: 150,
      align: 'center',
      disableColumnMenu: true,
      headerAlign: 'center',
      type: 'string'
    },
    {
      field: 'ModuleName',
      headerName: t('editConcept.Description'),
      flex: 2,
      disableColumnMenu: true,
      headerAlign: 'center',
      type: 'string'
    },
    {
      field: 'StartOfContract',
      headerName: t('editConcept.FromDate'),
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<any, string>) => renderStartDateOfContract(params, handleDateChange),
      align: 'center',
      disableColumnMenu: true,
      headerAlign: 'center'
    },
    {
      field: 'EndOfContract',
      headerName: t('editConcept.ToDate'),
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<any, string>) => rendeEndDateOfContract(params, handleDateChange),
      align: 'center',
      disableColumnMenu: true,
      headerAlign: 'center'
    },
    {
      field: 'ModuleType',
      headerName: t('editConcept.ModuleType'),
      flex: 1,
      renderCell: renderModuleType,
      align: 'center',
      disableColumnMenu: true,
      headerAlign: 'center',
      type: 'string'
    },
    {
      field: 'IsEnabled',
      headerName: t('editConcept.Is-available'),
      flex: 1,
      renderCell: renderIsActive,
      align: 'center',
      disableColumnMenu: true,
      headerAlign: 'center',
      type: 'boolean'
    }
  ]

  const handleSave = async () => {
    const appId = AppId
    try {
      setLoading(true)
      const body = {
        CustomerSubscriptions: {
          CustNo: customerId,
          ShipToLoc: shipToLoc,
          OuName: ou_name,
          AppID: Number(appId),
          CustomerEosId: custEosId
        },
        Subscriptions: updatedModules.map(module => ({
          ModuleID: module.ModuleId,
          StartOfContract: module.StartOfContract,
          EndOfContract: module.EndOfContract,
          Overridden: true,
          OverrideAction: module.IsEnabled ? 'enable' : 'disable'
        }))
      }

      await dispatch(saveConcepts(body))
      fetchConceptsData()
      setLoading(false)
      notifier.success('Changes updated successfully!')
      setUpdatedModules([])
    } catch (error) {
      setLoading(false)
      notifier.error('Error saving modules')
    }
  }
  return (
    <Paper ref={tableRef} sx={paperStyle}>
      <Box sx={headerRowStyle}>
        <Box sx={actionGroupStyle}>
          <TextField
            size='small'
            placeholder={t('editConcept.SearchPlaceholder')}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            sx={searchFieldStyle}
            inputProps={{ 'aria-label': t('editConcept.SearchPlaceholder') }}
          />
          <Button variant='contained' className='ui blue tiny button ml-half' onClick={handleSearch} sx={actionButtonStyle}>
            <i className='search icon' />
            {t('editConcept.Search')}
          </Button>
        </Box>

        <Box sx={{ flex: 4, textAlign: 'center' }}>{[customerId, custEosId, name, ou_name].filter(Boolean).join(' | ')}</Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link to={{ pathname: '/LKQ/Profile', state: { preserveProfileState: true } }}>
            <Button variant='outlined' color='inherit' className='ui tiny ml-half' sx={backButtonStyle}>
              <i className='arrow left icon' style={backIconStyle} />
              {t('module.Back')}
            </Button>
          </Link>
          <Button sx={saveButtonStyle} type='submit' data-testid='savebtn' className='ui blue tiny button ml-half' onClick={handleSave}>
            <i className='save icon' />
            {t('editConcept.Save')}
          </Button>
        </Box>
      </Box>
      <DataGridPro
        disableRowSelectionOnClick
        density='comfortable'
        getRowId={value => `${value.ModuleId}_${value.StartOfContract ?? ''}`}
        rows={filteredModules}
        columns={columns}
        rowsLoadingMode='client'
        columnHeaderHeight={25}
        hideFooter
        rowHeight={40}
        loading={loading}
        sx={gridStyle}
        getRowClassName={params => {
          const rowId = `${params.row.ModuleId}_${params.row.StartOfContract ?? ''}`
          if (rowId === activeRowId) {
            return 'activeRow'
          }
          if (params.row.Overridden) {
            return 'over-riddeden'
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? 'evenRow' : 'oddRow'
        }}
        onRowClick={params => {
          setActiveRowId(`${params.row.ModuleId}_${params.row.StartOfContract ?? ''}`)
        }}
        getCellClassName={() => 'cell-data'}
      />
    </Paper>
  )
}
export default ConceptManagement
